import { action, computed, makeObservable, observable } from 'mobx';
import { httpRequest } from '@/request';
import { BASE_STORE_URL, SERVER } from '@/request/config';
import { message } from 'antd';
import { cache, debounce, handleQueryParams, navigateToLogin, isBroswer } from '@/utils';
import md5 from 'md5';
import { enableStaticRendering } from 'mobx-react';
import { GlobalTracker } from '@/utils/Tracker';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import StoreBase from './Base';
import { isEmpty } from 'lodash';
import { encodeURIEncryptFE } from '@/utils/libs/JSEncrypt';

enableStaticRendering(typeof window === 'undefined');

class State extends StoreBase {
  constructor(props) {
    super(props);
    makeObservable(this);

    if (isBroswer) {
      this.handleAuthCache();
    }
  }

  @observable
  account = null;

  @observable
  instaToken = '';

  @observable
  guest = '';

  // 登录用户邮箱
  @observable
  email = '';

  // 登录/注册成功回调事件
  @observable
  loginSuccessCallback = () => {
    const { isOrderConfirmPage, isCloudUpgradeOrderConfirmPage } = this.root.app;

    // 结账页获取用户地址、用户优惠券列表
    // 云服务升级结账页不需要获取地址，直接使用云服务权益数据的地址信息。也不能使用优惠券。
    if (isOrderConfirmPage && !isCloudUpgradeOrderConfirmPage) {
      this.root.order.listDelivery();
      this.root.order.getUserGiftCards();
    }
  };

  // 是否完成初始化用户鉴权
  @observable
  initAuth = false;

  @computed
  get alreadyAuth() {
    return this.instaToken || this.guest;
  }

  @computed
  get isGuest() {
    return !this.instaToken && this.guest;
  }

  @computed
  get isLoggedIn() {
    return this.instaToken;
  }

  @action
  setStateValue = (state, value) => {
    this[state] = value;
  };

  @action
  requireAuthRedirect = (redirectUrl) => {
    return new Promise((resolve) => {
      if (this.alreadyAuth) {
        resolve(true);
      } else {
        navigateToLogin(redirectUrl);
      }
    });
  };

  /**
   *
   * @desc: 判断当前用户鉴权状态是否通过
   * @author: haifn
   * @time: 2021-11-22 6:56:43
   */
  @action
  handleAuthCache = () => {
    try {
      const { instaStorage } = this.root.app;
      const instaToken = cache.getCookie('insta_access_token');
      const userProfile = instaStorage.get('user_profile');

      // 不存在用户token
      if (!instaToken) {
        this.initAuth = true;
        this.removeAuth();
      }

      // 存在token进行校验
      if (instaToken) {
        this.instaToken = instaToken;
        // 不存在当前用户详情(首次登录状态访问商城)
        if (!userProfile) {
          // 官网登陆跳转回来
          GlobalTracker.UserAuthTracker({ action: 'login_success' });
          this.checkInstaAccess({});
        } else {
          if (instaStorage.get('insta_token') === instaToken) {
            this.initUserSession();
          } else {
            this.checkInstaAccess({});
          }
        }
      }
    } catch (e) {
      this.removeAuth();
    }
  };

  /**
   *
   * @description 验证/注册Store用户
   *
   *              1. 存在已注册用户，返回用户信息
   *              2. 不存在，注册store账户，返回用户信息
   *
   * @param {String} instaToken 用户user_token;
   * @param {String} type auto（访问站点自动校验） || manual（主动登录/注册校验）;
   * @param {Function} cb 回调;
   *
   * @return {type} value
   * @time 2022/10/17 18:22
   *
   */
  @action
  checkInstaAccess = ({ type = 'auto', cb }) => {
    if (!this.instaToken) {
      this.removeAuth();
      return;
    }
    const { instaStorage } = this.root.app;
    instaStorage.clear();
    httpRequest({
      url: SERVER.API_SERVER_USER + '/account/checkUserAccess',
      instaToken: true,
    }).then(({ code }) => {
      switch (code) {
        case 0:
          instaStorage.set('insta_token', this.instaToken);
          this.initUserSession();

          if (type !== 'auto') message.success(this.root.area.LANG['common.form.loginSuccess_msg'], 3);
          cb && cb();
          break;
        case 1001: // AccountNotFound
          this.removeAuth();
          if (type !== 'auto') message.warning(this.root.area.LANG['common.form.loginFailed_warningMsg']);
          break;
        case 1:
          this.removeAuth();
          if (type !== 'auto') message.warning(this.root.area.LANG['common.form.loginFailed_warningMsg']);

          // 自动校验，且当前页面为结账页，且用户登录态失效，就重新刷新一次。
          if (type === 'auto' && this.root.app.isOrderConfirmPage) window.location.reload();
          break;
        default:
          this.removeAuth();
          if (type !== 'auto') message.warning(this.root.area.LANG['common.form.loginFailed_warningMsg']);
      }
    });
  };

  /**
   * @description token已经设置后，根据token查询userProfile，进行登录成功的回调和初始化
   * 如果是结账页 / 购物车页 / 订阅管理页（需要实时展示用户的信息），更新详情。
   */
  @action
  initUserSession = async () => {
    const { isOrderConfirmPage, isCartPage, isSubscriptionPage } = this.root.app;
    this.instaToken = cache.getCookie('insta_access_token');

    const needUpdate = isOrderConfirmPage || isCartPage || isSubscriptionPage;
    await this.getUserProfile(needUpdate);

    try {
      const { instaStorage, isCartPage } = this.root.app;

      let userProfile = instaStorage.get('user_profile', { type: 'object' });

      this.email = userProfile.email;
      this.account = userProfile;

      if (isBroswer) {
        instaStorage.remove('guest_profile'); // 已登陆、移除访客信息
        // intercom信息
        this.initIntercom();
        // 初始化arms信息
        window.monitorCore && window.monitorCore.setUser(encodeURIEncryptFE(this.email));

        this.initAuth = true;
        window.dispatchEvent(new CustomEvent('login'));

        // 执行登录成功回调
        // 由于没有商城直接登陆的场景，全部都会跳转到官网登陆再跳转回来，所以不需要中途请求信息。因此相关方法挪动到对应页面的初始化执行（虽然也只有结账页）
        // if (this.loginSuccessCallback) {
        //   this.loginSuccessCallback();
        //   this.loginSuccessCallback = null;
        // }

        // 登陆用户获取权益期数据
        this.root.app.getCloudSubscribeDetail(isCartPage ? 'cartPage' : 'loginCallback');

        // 同步购物车数据
        this.root.order?.syncCartDetail();
        // 获取分销商信息数据
        this.root.reseller.getResellerInfo();
      }
    } catch (e) {
      armsErrorSend(e, { filename: 'init user session error' });
    }
  };

  /**
   *
   * @description 获取用户信息
   * @return {type} value
   * @time 2022/10/18 10:30
   *
   */
  @action
  getUserProfile = (needUpdate) => {
    const { instaStorage } = this.root.app;
    const userProfile = instaStorage.get('user_profile', { type: 'object' });
    if (!isEmpty(userProfile) && !needUpdate) return;
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      const { instaStorage } = this.root.app;
      const res = await httpRequest({ url: SERVER.API_SERVER_ACCOUNT + '/getUserProfileByToken', instaToken: true });
      if (res.code === 0) {
        cache.setCookie('insta360.account.id', res.data.account.id, { expires: 30 }); // 过期时间跟着token
        instaStorage.set('user_profile', res.data.account);
        instaStorage.set('store_access', {
          accountId: res.data.account.id,
          email: res.data.account.email,
        });
      }

      resolve();
    });
  };

  /*
   *
   * @desc: 内部数据收集、事件上报接口
   * @author: haifn
   * @time: 2021-11-22 7:0:54
   */
  @action
  dataCollect = ({ event, data = {}, cb }) => {
    // 开发环境test接口404，不上报
    if (!this.root.app.isProd) {
      console.log('dataCollect', {
        event,
        data,
      });
      return;
    }

    data = Object.assign(data, {
      account: this.account && this.account.id,
      email: this.email || '',
      guest: this.guest || '',
      // tk: this.instaToken || '',
      country: this.root.area.country.code,
      env: window.isProd ? 'prod' : 'test',
      platform: this.root.app.isMobile ? 'mob' : 'pc',
    });
    httpRequest({
      url: SERVER.API_SERVER_DATA_COLLECTION + '/collect',
      method: 'post',
      data: {
        app: 'official_store',
        records: [
          {
            event,
            data,
            timestamp: new Date().getTime(),
          },
        ],
      },
    })
      .then(() => {
        cb && cb();
      })
      .catch(() => {
        cb && cb();
      });
  };

  // CA、US地区需要记录手机号-已弃用
  subscribePhoneNumber = (data) => {
    const url = BASE_STORE_URL + '/user/subscribe/subscribePhone';
    return httpRequest({
      url,
      method: 'post',
      data: data,
    });
  };

  // 检查邮箱是否重复订阅
  emailRepeatCheck = (data) => {
    const url = BASE_STORE_URL + '/user/subscribe/repeatCheck';
    return httpRequest({
      url,
      method: 'post',
      data: data,
    });
  };

  /**
   *
   * @desc: 邮箱订阅
   * @author: haifn
   * @time: 2021-11-22 7:1:34
   */
  @action
  emailSubscribe = (data, cb) => {
    const url = BASE_STORE_URL + '/user/subscribe/submitSubscribe';
    let LocationPath = window.location.pathname;
    const LANG = this.root?.area?.LANG;
    // 走代金券模版领用的页面
    const giftCardTemplatePage = ['go2_mtb', 'x3_mtc', 'country_subscribe'];

    function handleActivityPage() {
      // 从外部传进来则取参数值
      if (data.activityPage) return data.activityPage;
      // 否则取默认配置
      const storeSubscriptionMap = {
        '': 'homepage',
        '/tradeup': 'tradeup',
        '/affiliate/intro': 'affiliate',

        '/enterprise': 'enterprise',
        '/product/virtual_tour_kit': 'enterprise',
        '/product/8k_live': 'enterprise',

        '/product/sphere': 'sphere',
        '/product/one_rs': 'one_rs',
        '/product/one_x2': 'one_x2',
        '/product/one_r': 'one_r',
        '/product/go': 'go',
        '/product/one_x': 'one_x',
        '/product/evo': 'evo',
        '/product/one': 'one',
        '/product/nanos': 'nano_s',
        '/product/titan': 'titan',
        '/product/Pro_2': 'pro_2',
        '/product/pro': 'pro',

        '/insta360BikeDeal': 'bike_kit_deal',
        '/product/care': 'care',
      };
      // 移除结尾的斜杠字符
      if (LocationPath[LocationPath.length - 1] === '/') {
        LocationPath = LocationPath.slice(0, LocationPath.length - 1);
      }
      return storeSubscriptionMap[LocationPath] || 'other_footer';
    }

    function handleSubscribePage() {
      // 列表内的路由只显示配置值，避免:numberId加密串被记录
      const PathRuleGroup = [
        '/order/detail',
        '/order/trace',
        '/order/pay',
        '/order/transfer',
        '/order/result',
        '/order/weixinpay',
        '/rma/detail',
      ];
      const rulePath = PathRuleGroup.find((path) => LocationPath?.toLocaleLowerCase()?.includes(path));

      const getUrl = (locationPath) => {
        const paths = locationPath.split('/') ?? [];
        return paths
          .reduce((pre, cur, index) => {
            if (cur.length < 50) {
              pre.push(cur);
            } else {
              // 字符长度超过50，之后的全部舍弃
              paths.length = index;
            }
            return pre;
          }, [])
          .join('/');
      };

      return rulePath ?? getUrl(LocationPath);
    }

    function handleGoogleField() {
      const zc_gad = document.getElementById('zc_gad');
      return zc_gad ? zc_gad?.value : '';
    }

    const postData = {
      email: data.email,
      activityPage: handleActivityPage(),
      subscribePage: handleSubscribePage(),
      googleField: handleGoogleField(),
      phoneNumber: data.phoneNumber ?? '',
      interestList: data.interestList ?? [],
    };

    return httpRequest({
      url,
      method: 'post',
      data: postData,
    }).then((res) => {
      GlobalTracker.EmailSubscribeSubmit({
        activityPage: postData?.activityPage,
        success: res.code === 0,
        email: postData?.email,
      });

      cb && cb();

      // 对代金券模版过期的情况进行处理
      if (giftCardTemplatePage.includes(postData?.activityPage) && res.code === 0) {
        const { templateInvalidFlag = null } = res.data;
        if (templateInvalidFlag) {
          message.success(LANG['common.tips.fetch_giftCardCode_error'] || '');
          return Promise.reject(res);
        } else return Promise.resolve(res);
      } else {
        return Promise.resolve(res);
      }
    });
  };

  // 用户黑五活动页电话订阅
  @action
  phoneSubscribeCB = (data) => {
    const url = BASE_STORE_URL + '/user/subscribe/subscribePhone';
    // const

    return httpRequest({
      url,
      method: 'post',
      data,
    });
  };

  // 客户端轮询获取电话订阅结果
  @action
  pollingPhoneSubscribeResult = (params) => {
    const url = BASE_STORE_URL + '/user/subscribe/getResult';

    return httpRequest({
      url,
      method: 'get',
      params,
    });
  };

  // 优惠订阅弹窗中US地区订阅
  @action
  phoneSubscribe = (data) => {
    const url = BASE_STORE_URL + '/user/subscribe/subscribeBf';
    let LocationPath = window.location.pathname;
    function handleSubscribePage() {
      // 列表内的路由只显示配置值，避免:numberId加密串被记录
      const PathRuleGroup = [
        '/order/detail',
        '/order/trace',
        '/order/pay',
        '/order/transfer',
        '/order/result',
        '/order/weixinpay',
        '/rma/detail',
      ];
      const rulePath = PathRuleGroup.find((path) => LocationPath?.toLocaleLowerCase()?.includes(path));

      const getUrl = (locationPath) => {
        const paths = locationPath.split('/') ?? [];
        return paths
          .reduce((pre, cur, index) => {
            if (cur.length < 50) {
              pre.push(cur);
            } else {
              // 字符长度超过50，之后的全部舍弃
              paths.length = index;
            }
            return pre;
          }, [])
          .join('/');
      };

      return rulePath ?? getUrl(LocationPath);
    }
    function handleGoogleField() {
      const zc_gad = document.getElementById('zc_gad');
      return zc_gad ? zc_gad?.value : '';
    }
    const postData = {
      email: data.email,
      activityPage: data.activityPage,
      subscribePage: handleSubscribePage(),
      googleField: handleGoogleField(),
      phoneNumber: data.phone ?? '',
      interestList: data.interestList ?? [],
    };
    return httpRequest({
      url,
      method: 'post',
      data: postData,
    });
  };

  /**
   *
   * @description 退出登录
   */
  @action
  logout = () => {
    this.removeAuth();
    this.root.order?.clearCart();
    // 去除url后的hash值，否则页面不刷新
    window.location.href = handleQueryParams()?.replace(window.location.hash, '');
  };

  /**
   *
   * @description 移除用户登录信息
   */
  @action
  removeAuth = () => {
    const { instaStorage } = this.root.app;
    instaStorage.clear();
    cache.removeCookie('insta_access_token');
    cache.removeCookie('insta_cart_synced');
    cache.removeCookie('insta360.account.id');
    this.instaToken = '';
    // 不存在云服务权益
    this.root.app.setGlobalValue('cloudSubscribeInfo', {});
    // 清除云服务权益数据
    cache.set('cloud_upgrade_items', JSON.stringify([]));
    cache.removeSessionItem('cloud_subscribe_info');
    cache.removeSessionItem('cloud_subscribe_init');
  };

  /*
   *
   * @desc: 初始化客服工具客户联系方式
   * @time: 2021/11/22 10:01 上午
   *
   * @update: 移除udesk init逻辑
   * @time: 2024/10/22
   */
  initIntercom = () => {
    const email = this.guest || this.email || '';
    if (!email) return '';
    const { language } = this.root.area.country;
    // 简中/繁中使用udesk（官网链接）
    if (!['zh_cn', 'zh_tw'].includes(language)) {
      if (window.Intercom && window.intercomSettings) {
        console.log('intercom boot：', email);
        window.intercomSettings.email = email;
        window.intercomSettings.user_id = email;
        window.Intercom('update', window.intercomSettings);
      }
    }
  };

  /*
   *
   * @desc: 设置游客邮箱
   *        初始化客服联系方式
   * @time: 2021-11-22 11:6:26
   */
  @action
  handleGuestSubmit = ({ email }) => {
    this.guest = email;
    this.initIntercom();
    if (isBroswer) {
      window.sessionStorage.setItem('guestProfile', JSON.stringify({ email }));
    }
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  /*
   *
   * @desc: 登录
   * @author: haifn
   * @time: 2021-11-22 3:59:10
   */
  @action
  handleLoginSubmit = debounce(({ email, password }, callback) => {
    httpRequest({
      url: SERVER.API_SERVER_ACCOUNT_V2 + '/signin',
      method: 'post',
      data: {
        username: email,
        password: md5(password),
      },
    }).then((res) => {
      if (res.code !== 0) {
        switch (res.code) {
          case 1001:
            message.warn(this.root.area.LANG['common.form.account_notFound_msg']);
            break;
          case 1002:
            message.warn(this.root.area.LANG['common.form.password_error_msg']);
            break;
          default:
            message.warn(res.code, res.error);
        }
        return;
      }
      cache.setCookie('insta_access_token', res.data.token, { expires: 30 });
      cache.setCookie('insta360.account.id', res.data.account.id, { expires: 30 }); // 过期时间跟着token
      this.instaToken = res.data.token;
      this.checkInstaAccess({ type: 'login' });
      callback?.('login');
      GlobalTracker.UserAuthTracker({ action: 'user_login' });
    });
  }, 300);

  /*
   *
   * @desc: 注册
   * @author: haifn
   * @time: 2021-11-22 5:47:42
   */
  @action
  handleRegisterSubmit = debounce(({ email, password }) => {
    this.root.app.setGlobalValue('G_GlobalLoading', true);
    httpRequest({
      url: SERVER.API_SERVER_ACCOUNT_V2 + '/signup',
      method: 'post',
      data: {
        username: email,
        password: md5(password),
        source: 'mall',
      },
    }).then((res) => {
      if (res.code !== 0) {
        switch (res.code) {
          case 1000:
            message.warn(this.root.area.LANG['common.form.register_emailExit_msg']);
            break;
          default:
            message.warn(res.code, res.error);
        }
        this.root.app.setGlobalValue('G_GlobalLoading', false);
        return;
      }
      // 延迟500ms执行，避免注册成功以后，RPC通知商城注册用户未执行完成
      setTimeout(() => {
        cache.setCookie('insta_access_token', res.data.token, { expires: 30 });
        cache.setCookie('insta360.authed.email', email, { expires: 30 });
        this.instaToken = res.data.token;
        this.root.auth.checkInstaAccess({ type: 'register' });
        this.root.app.setGlobalValue('G_GlobalLoading', false);
        GlobalTracker.UserAuthTracker({ action: 'user_register' });
      }, 500);
    });
  }, 300);

  /*
   *
   * @desc: 重置密码
   * @author: haifn
   * @time: 2021-11-22 6:28:52
   */
  @action
  handleResetPasswordSubmit = debounce(({ email, cb }) => {
    httpRequest({
      url: SERVER.API_SERVER_ACCOUNT + '/secure/forgetPassword',
      method: 'post',
      data: {
        username: email,
        redirect_url: window.location.href,
      },
    }).then((res) => {
      if (res.code !== 0) {
        switch (res.code) {
          case 1001:
            message.warn(this.root.area.LANG['common.form.account_notFound_msg']);
            break;
          default:
            message.warn(res.code, res.error);
        }
        return;
      }
      GlobalTracker.UserAuthTracker({ action: 'user_reset' });
      message.success(this.root.area.LANG['common.tips.resetPasswordEmail_success'], 5);
      cb && cb();
    });
  }, 300);
}

export default State;
